import { parseToDateValue } from "@/features/FormCard/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef } from "react";
import {
  FieldValues,
  Path,
  PathValue,
  Resolver,
  useForm,
} from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import { ZodSchema } from "zod";

export type ResetSpec = Record<string, string | null>;
interface UseFilterTableProps<T> {
  schema: ZodSchema<T>;
  client: string;
  tableName: string;
  /**
   * Custom function to build the URL.
   * If not provided, the default URL will be `/admin/${client}/${tableName}`
   * The query params will be added to the URL after the path.
   */
  customBuildPath?: () => string;
  /**
   * Fields to exclude from date conversion.
   * If not provided, all fields will be converted to date.
   */
  excludeFieldsFromConvertDate?: string[];
  /** Always apply on submit (e.g. force page=1 every time) */
  resetParamsAlways?: ResetSpec;
  /** Apply only if any filter value changed vs current URL (typical: { page: "1" }) */
  resetParamsIfFiltersChanged?: ResetSpec;
  /** Also apply on handleReset() (e.g. clear page & sort) */
  resetParamsOnHandleReset?: ResetSpec;
}

/**
 * Hook to manage filter state in table components with URL synchronization.
 *
 * @template T - Type of form values, extends FieldValues from react-hook-form
 * @param {UseFilterTableProps<T>} props - Configuration options
 * @param {ZodSchema<T>} props.schema - Zod schema for form validation
 * @param {string} props.client - Client identifier used in URL construction
 * @param {string} props.tableName - Table name used in URL construction
 * @param {Function} [props.customBuildPath] - Optional function to build custom URL path
 * @returns {Object} Form control methods and handlers
 * @returns {Function} returns.register - react-hook-form register function
 * @returns {Function} returns.handleSubmit - react-hook-form submit handler
 * @returns {Function} returns.reset - Function to reset form values
 * @returns {Function} returns.setValue - Function to set specific form value
 * @returns {Function} returns.watch - Function to watch form values
 * @returns {Function} returns.onSubmit - Handler to submit form and update URL
 * @returns {Function} returns.handleReset - Handler to reset form state
 */
export function useFilterTable<T extends FieldValues>({
  schema,
  tableName,
  client,
  customBuildPath,
  excludeFieldsFromConvertDate,
  resetParamsAlways,
  resetParamsIfFiltersChanged,
  resetParamsOnHandleReset,
}: UseFilterTableProps<T>) {
  const { register, handleSubmit, reset, getValues, setValue, watch } =
    useForm<T>({
      resolver: zodResolver(schema as any) as unknown as Resolver<T>,
    });

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const toWorkspaceAdminFilterPath = (rawPath: string) => {
    const trimmed = rawPath.trim();
    const absolute = trimmed.startsWith("/")
      ? trimmed
      : `/admin/${trimmed.replace(/^\/+/, "")}`;
    return toWorkspacePath(absolute);
  };

  // Track form field names
  const formFieldsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    searchParams.forEach((value, key) => {
      // Track this as a form field
      formFieldsRef.current.add(key);

      if (
        (key.includes("date") || key.includes("create")) &&
        value.split("-").length === 3 &&
        !excludeFieldsFromConvertDate?.includes(key)
      ) {
        const dateValue = parseToDateValue(value);
        setValue(key as Path<T>, dateValue as PathValue<T, Path<T>>);
      } else {
        setValue(key as Path<T>, value as any);
      }
    });
  }, [searchParams, setValue]);

  const applyResetSpec = (params: URLSearchParams, spec?: ResetSpec) => {
    if (!spec) return;
    for (const [k, v] of Object.entries(spec)) {
      if (v == null) params.delete(k);
      else params.set(k, v);
    }
  };

  const didFiltersChange = (
    before: URLSearchParams,
    after: URLSearchParams,
  ) => {
    // Compare only fields this form manages
    for (const key of formFieldsRef.current) {
      if (before.get(key) !== after.get(key)) return true;
    }
    return false;
  };

  const onSubmit = (filters: T) => {
    // Get current search params to preserve existing filters
    const currentParams = new URLSearchParams(searchParams);
    const nextParams = new URLSearchParams(currentParams);

    // Update or add form filter values
    Object.entries(filters).forEach(([key, value]) => {
      const v = value as unknown;
      if (v === undefined || v === null || v === "") {
        nextParams.delete(key);
      } else {
        nextParams.set(key, String(v));
      }
      // track as managed by this form
      formFieldsRef.current.add(key);
    });

    // always apply (e.g. page=1 every submit)
    applyResetSpec(nextParams, resetParamsAlways);

    // only if something actually changed
    if (didFiltersChange(currentParams, nextParams)) {
      applyResetSpec(nextParams, resetParamsIfFiltersChanged);
    }

    const queryParams = nextParams.toString();
    if (customBuildPath) {
      navigate(
        toWorkspaceAdminFilterPath(`${customBuildPath()}?${queryParams}`),
      );
    } else {
      navigate(toWorkspacePath(`/admin/${client}/${tableName}?${queryParams}`));
    }
  };

  const handleReset = () => {
    reset();

    // Get current search params to preserve non-form filters
    const currentParams = new URLSearchParams(searchParams);

    // Remove form field parameters from URL
    formFieldsRef.current.forEach((field) => {
      currentParams.delete(field);
    });

    applyResetSpec(currentParams, resetParamsOnHandleReset);

    // Clear the tracked form fields
    formFieldsRef.current.clear();

    const queryParams = currentParams.toString();
    if (customBuildPath) {
      navigate(
        toWorkspaceAdminFilterPath(
          `${customBuildPath()}${queryParams ? `?${queryParams}` : ""}`,
        ),
      );
    } else {
      navigate(
        toWorkspacePath(
          `/admin/${client}/${tableName}${queryParams ? `?${queryParams}` : ""}`,
        ),
      );
    }
  };

  return {
    register,
    handleSubmit,
    reset,
    getValues,
    setValue,
    watch,
    onSubmit,
    handleReset,
  };
}
